import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, Clock, FileText, Globe, ImagePlus, Loader2, RotateCcw, Sparkles,
  Upload, Wand2, X, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import AIMenuGeneratorModal from './AIMenuGeneratorModal';
import MenuDraftReview, { PublishedSummary } from './MenuDraftReview';
import { getBranchAuthHeaders } from '@/utils/branchApi';
import { boPath } from '@/utils/getBasePath';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_FILES = 10;
const MAX_BYTES = 25 * 1024 * 1024;
const WORKING = ['CREATED', 'UPLOADED', 'EXTRACTING', 'PARSING', 'APPLYING'];

const STATUS = {
  CREATED: { label: 'Queued', cls: 'bg-slate-500' },
  UPLOADED: { label: 'Queued', cls: 'bg-slate-500' },
  EXTRACTING: { label: 'Reading', cls: 'bg-sky-600' },
  PARSING: { label: 'AI reading', cls: 'bg-violet-600' },
  NEEDS_OCR: { label: 'Needs reading', cls: 'bg-orange-500' },
  READY_FOR_REVIEW: { label: 'Ready to review', cls: 'bg-emerald-600' },
  APPLYING: { label: 'Publishing', cls: 'bg-sky-600' },
  COMPLETED: { label: 'Published', cls: 'bg-emerald-700' },
  FAILED: { label: 'Failed', cls: 'bg-red-600' },
  ROLLED_BACK: { label: 'Undone', cls: 'bg-amber-600' },
};

const STEPS = [
  ['upload', 'Upload'],
  ['reading', 'AI reads the menu'],
  ['photos', 'Dish photos'],
  ['ready', 'Review & publish'],
];

function errorText(detail, fallback) {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return detail.message;
  return fallback;
}

function stepIndex(job) {
  if (!job) return 0;
  if (['READY_FOR_REVIEW', 'COMPLETED', 'ROLLED_BACK'].includes(job.status)) return 3;
  const stage = job.progress?.stage;
  if (stage === 'photos') return 2;
  if (stage === 'reading' || job.status === 'PARSING') return 1;
  return job.status === 'FAILED' ? -1 : 1;
}

function Progress({ job }) {
  const idx = stepIndex(job);
  const p = job.progress || {};
  const failed = job.status === 'FAILED';
  const pct = p.total ? Math.round((100 * (p.done || 0)) / p.total) : null;
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4" data-testid="import-progress">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Importing</div>
          <div className="font-semibold truncate">{job.sourceFilename}</div>
        </div>
        <Badge className={`${(STATUS[job.status] || STATUS.CREATED).cls} text-white`}>{(STATUS[job.status] || STATUS.CREATED).label}</Badge>
      </div>
      <ol className="grid grid-cols-4 gap-2">
        {STEPS.map(([key, label], i) => {
          const done = !failed && i < idx;
          const now = !failed && i === idx && WORKING.includes(job.status);
          return (
            <li key={key} className="space-y-1.5">
              <div className={`h-1.5 rounded-full ${done ? 'bg-emerald-500' : now ? 'bg-primary animate-pulse' : failed ? 'bg-red-500/30' : 'bg-muted'}`} />
              <div className={`text-xs ${done || now ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{label}</div>
            </li>
          );
        })}
      </ol>
      {failed ? (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="import-failed">
          <div className="flex items-center gap-2 font-semibold text-red-700 dark:text-red-300"><XCircle className="h-4 w-4" />The menu could not be built</div>
          <p className="mt-1 text-sm">{job.errorMessage || 'Unknown error'}</p>
          {job.errorCode ? <p className="mt-1 text-xs text-muted-foreground">Code: {job.errorCode}</p> : null}
        </div>
      ) : (
        <div className="flex items-center gap-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span data-testid="import-progress-message">{p.message || 'Working…'}</span>
          {pct != null ? <span className="ml-auto tabular-nums text-muted-foreground">{p.done}/{p.total}</span> : null}
        </div>
      )}
    </div>
  );
}

export default function MenuImportPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(null);           // 'upload' | 'url' | null
  const [dragging, setDragging] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [published, setPublished] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const fileInput = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs?limit=20`, { headers: getBranchAuthHeaders() });
      if (res.ok) setJobs((await res.json()).jobs || []);
    } catch (e) { /* list is secondary */ }
  }, []);

  const openJob = useCallback(async (id) => {
    const res = await fetch(`${API_URL}/api/menu-import/jobs/${id}?includeContent=true`, { headers: getBranchAuthHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    setJob(data);
    return data;
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // honest progress: poll while the server is working
  useEffect(() => {
    if (!job || !WORKING.includes(job.status)) return undefined;
    const t = setInterval(async () => {
      const d = await openJob(job.id);
      if (d && !WORKING.includes(d.status)) {
        fetchJobs();
        if (d.status === 'READY_FOR_REVIEW') toast.success(d.progress?.message || 'Your menu is ready to review');
        if (d.status === 'FAILED') toast.error(d.errorMessage || 'The menu could not be built');
      }
    }, 2000);
    return () => clearInterval(t);
  }, [job, openJob, fetchJobs]);

  useEffect(() => () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)), [previews]);

  const pickFiles = (list) => {
    const chosen = Array.from(list || []);
    const ok = [];
    for (const f of chosen) {
      if (!ACCEPT.split(',').includes(f.type)) { toast.error(`${f.name}: upload JPEG, PNG, WebP photos or a PDF`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} is larger than 25MB`); continue; }
      ok.push(f);
    }
    const next = [...files, ...ok].slice(0, MAX_FILES);
    if (files.length + ok.length > MAX_FILES) toast.error(`At most ${MAX_FILES} files per import`);
    setFiles(next);
    setPreviews(next.map((f) => ({ name: f.name, pdf: f.type === 'application/pdf', url: f.type === 'application/pdf' ? null : URL.createObjectURL(f) })));
  };

  const startUpload = async () => {
    if (!files.length) return;
    setBusy('upload');
    setPublished(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API_URL}/api/menu-import/jobs/files`, { method: 'POST', headers: getBranchAuthHeaders(), body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(errorText(body.detail, 'The upload failed')); return; }
      setFiles([]); setPreviews([]);
      await openJob(body.jobId);
      fetchJobs();
    } catch (e) {
      toast.error('The upload failed - check your connection and try again');
    } finally { setBusy(null); }
  };

  const startUrl = async () => {
    if (!url.trim()) return;
    setBusy('url');
    setPublished(null);
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs/from-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getBranchAuthHeaders() },
        body: JSON.stringify({ url: url.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(errorText(body.detail, 'That website could not be read')); return; }
      setUrl('');
      await openJob(body.jobId);
      fetchJobs();
    } catch (e) {
      toast.error('That website could not be read');
    } finally { setBusy(null); }
  };

  const undo = async () => {
    if (!job) return;
    if (!window.confirm('Undo this import? Items it added are removed and items it updated get their old values back.')) return;
    setUndoing(true);
    try {
      const res = await fetch(`${API_URL}/api/menu-import/jobs/${job.id}/rollback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getBranchAuthHeaders() },
        body: JSON.stringify({ reason: 'Owner undid the import' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(errorText(body.detail, 'Undo failed')); return; }
      toast.success(body.message || 'Import undone');
      setPublished(null);
      await openJob(job.id);
      fetchJobs();
    } finally { setUndoing(false); }
  };

  const working = job && WORKING.includes(job.status);

  return (
    <div className="space-y-6 pb-8" data-testid="menu-import-page">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" />AI Menu Builder
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight">Build your menu from a photo or your website</h1>
          <p className="mt-1 max-w-2xl text-muted-foreground">
            Upload your whole menu — one photo, several pages or a PDF — or paste your website. AI reads every dish,
            price and section, crops the dish photos, and you review everything before it goes live on online ordering,
            the kiosk and the POS.
          </p>
        </div>
        <Button variant="outline" onClick={() => setShowGenerator(true)} data-testid="ai-generate-btn">
          <Wand2 className="h-4 w-4 mr-2" />Describe a menu instead
        </Button>
      </div>

      {/* sources */}
      {!job || !['READY_FOR_REVIEW'].includes(job.status) ? (
        <div className="grid gap-5 lg:grid-cols-5">
          <div
            className={`lg:col-span-3 rounded-2xl border-2 border-dashed p-6 transition ${dragging ? 'border-primary bg-primary/5' : 'border-border bg-card'}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pickFiles(e.dataTransfer.files); }}
            data-testid="upload-dropzone"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><ImagePlus className="h-6 w-6" /></div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Upload your menu</h3>
                <p className="text-sm text-muted-foreground">A photo of the whole menu is enough. Add more photos or a PDF for a multi-page menu (up to {MAX_FILES} files, 12 pages).</p>
              </div>
            </div>
            <input ref={fileInput} id="menu-file-input" type="file" accept={ACCEPT} multiple className="hidden"
                   onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }} data-testid="menu-file-input" />
            {previews.length ? (
              <div className="mt-5 flex flex-wrap gap-3">
                {previews.map((p, i) => (
                  <div key={i} className="relative h-28 w-24 overflow-hidden rounded-lg border bg-muted">
                    {p.pdf ? (
                      <div className="flex h-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground"><FileText className="h-7 w-7" />PDF</div>
                    ) : <img src={p.url} alt={p.name} className="h-full w-full object-cover" />}
                    <button type="button" className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                            onClick={() => { const n = files.filter((_, j) => j !== i); setFiles(n); setPreviews(previews.filter((_, j) => j !== i)); }}
                            aria-label="Remove file"><X className="h-3 w-3" /></button>
                    <div className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1 text-[10px] text-white">{p.name}</div>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={!!busy || working} data-testid="choose-files">
                <Upload className="h-4 w-4 mr-2" />{files.length ? 'Add more' : 'Choose photos or PDF'}
              </Button>
              <Button onClick={startUpload} disabled={!files.length || !!busy || working} data-testid="build-menu">
                {busy === 'upload' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Build my menu{files.length ? ` (${files.length} file${files.length > 1 ? 's' : ''})` : ''}
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl border bg-card p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-sky-600"><Globe className="h-6 w-6" /></div>
              <div>
                <h3 className="text-lg font-semibold">From your website</h3>
                <p className="text-sm text-muted-foreground">Your website, its menu page or a menu PDF link. We read the page, its Menu pages and linked menus.</p>
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <Input type="url" placeholder="https://yourrestaurant.com/menu" value={url} onChange={(e) => setUrl(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && startUrl()} disabled={!!busy || working} data-testid="url-input" />
              <Button onClick={startUrl} disabled={!url.trim() || !!busy || working} data-testid="crawl-url-btn">
                {busy === 'url' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Read'}
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">If your site builds its menu with JavaScript or blocks visitors, upload a photo instead.</p>
          </div>
        </div>
      ) : null}

      {job && (WORKING.includes(job.status) || job.status === 'FAILED') ? <Progress job={job} /> : null}

      {job && job.status === 'READY_FOR_REVIEW' && job.draft ? (
        <MenuDraftReview
          key={job.id}
          job={job}
          onPublished={async (result) => { setPublished(result); toast.success('Your menu is live'); await openJob(job.id); fetchJobs(); }}
        />
      ) : null}

      {job && job.status === 'COMPLETED' ? (
        <PublishedSummary
          result={published || {
            created: job.importStats?.created || { items: 0, categories: 0 },
            updated: job.importStats?.updated || { items: 0 },
            skipped: job.importStats?.skipped || 0, photos: job.importStats?.photos || 0,
            warnings: job.importStats?.warnings || [],
          }}
          onUndo={undo}
          undoing={undoing}
          onViewMenu={() => navigate(boPath('menu'))}
        />
      ) : null}

      {/* history */}
      <div className="rounded-2xl border bg-card">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">Recent imports</h3>
          {job ? <Button variant="ghost" size="sm" onClick={() => { setJob(null); setPublished(null); }}>New import</Button> : null}
        </div>
        {jobs.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">No imports yet — upload your menu to get started.</div>
        ) : (
          <ul className="divide-y">
            {jobs.map((j) => {
              const s = STATUS[j.status] || STATUS.CREATED;
              return (
                <li key={j.id}>
                  <button type="button" onClick={() => { setPublished(null); openJob(j.id); }}
                          className={`flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-muted/50 ${job?.id === j.id ? 'bg-muted/60' : ''}`}
                          data-testid={`job-${j.id}`}>
                    {j.status === 'COMPLETED' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : j.status === 'FAILED' ? <AlertTriangle className="h-4 w-4 text-red-600" />
                        : j.status === 'ROLLED_BACK' ? <RotateCcw className="h-4 w-4 text-amber-600" />
                          : <Clock className="h-4 w-4 text-muted-foreground" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{j.sourceFilename}</div>
                      <div className="text-xs text-muted-foreground">{j.createdAt ? new Date(j.createdAt).toLocaleString() : ''}</div>
                    </div>
                    <Badge className={`${s.cls} text-white`}>{s.label}</Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AIMenuGeneratorModal
        isOpen={showGenerator}
        onClose={() => setShowGenerator(false)}
        onMenuGenerated={async (data) => { if (data.jobId) { setPublished(null); await openJob(data.jobId); fetchJobs(); } }}
      />
    </div>
  );
}
